import moment from 'moment';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { API_DATE_FORMAT } from '../../constants';
import * as api from '../../services/api';
import { mappedArray } from '../../utils/array';
import { generateRange } from '../../utils/time';
import useAccount from './useAccount';

const getDate = (date) => moment(date).format(API_DATE_FORMAT);

const getShiftsInProjectByTimeRange = ({ project, timeRange, includeShiftEndDate = false, singleDayViewDate }) => {
  if (!project || !timeRange || !timeRange.startDate || !timeRange.endDate) {
    return [];
  }

  const fromRange = moment(timeRange.startDate);
  const toRange = moment(timeRange.endDate);

  return (project?.shifts || []).filter(({ date: shiftStartDate, endTime }) => {
    const dateMomentOfStartTime = moment(shiftStartDate, API_DATE_FORMAT);
    const shiftEndDate = moment(endTime).format(API_DATE_FORMAT);

    const isShiftStartDateInRange =
      dateMomentOfStartTime.isSameOrAfter(fromRange) && dateMomentOfStartTime.isSameOrBefore(toRange);

    if (includeShiftEndDate && !!singleDayViewDate) {
      return isShiftStartDateInRange && (shiftEndDate === singleDayViewDate || shiftStartDate === singleDayViewDate);
    }

    return isShiftStartDateInRange;
  });
};

const useProjects = ({
  branchIds,
  timeRange,
  filters,
  allEmployees,
  isArchived,
  projectId,
  filterProjects,
  full = false,
  enabled = true,
  refetchInterval = false,
  includeShiftEndDate = false,
  singleDayViewDate,
  showAllBranches,
  shiftDates
}) => {
  const { accountId } = useAccount();

  const storeKey = useMemo(
    () => ['projects', accountId, branchIds, showAllBranches, timeRange, filters, allEmployees, isArchived, shiftDates],
    [accountId, allEmployees, branchIds, filters, isArchived, showAllBranches, timeRange, shiftDates]
  );

  const { isLoading, data, refetch } = useQuery(
    storeKey,
    async () => {
      if (!projectId) {
        return api.fetchProjects({
          accountId,
          branchIds,
          from: moment(timeRange.startDate).format(API_DATE_FORMAT),
          to: moment(timeRange.endDate).format(API_DATE_FORMAT),
          allEmployees,
          filters,
          isArchived,
          full,
          showAllBranches,
          shiftDates
        });
      }
      const result = await api.fetchProject({
        accountId,
        branchId: branchIds[0],
        projectId
      });
      return {
        employees: result?.data?.employees,
        projects: [result?.data?.project],
        unbookings: result?.data?.unbookings
      };
    },

    {
      enabled: branchIds.length > 0 && !!(timeRange || projectId) && enabled,
      staleTime: 0,
      refetchInterval,
      notifyOnStatusChange: ['isLoading']
    }
  );

  const employeesMap = useMemo(() => mappedArray(data?.employees || [], ({ id }) => id), [data?.employees]);

  const projectsWithAllShiftsDates = useMemo(
    () => (filterProjects ? filterProjects(data?.projects || [], employeesMap) : data?.projects || []),

    [data?.projects, employeesMap, filterProjects]
  );

  const projects = useMemo(() => {
    return (projectsWithAllShiftsDates || []).map((project) => ({
      ...project,
      shifts: getShiftsInProjectByTimeRange({ project, timeRange, includeShiftEndDate, singleDayViewDate }) || [],
      mappedShiftsByDay: (project.shifts || []).reduce((all, shift) => {
        const shiftDate = shift.date;
        if (!all[shiftDate]) {
          all[shiftDate] = [];
        }

        all[shiftDate].push(shift);
        return all;
      }, {}),
      orders: project?.orders?.map((order) => ({
        ...order,
        mappedOrderShiftsByDay: (order.shifts || []).reduce((all, orderShift) => {
          const orderShiftDate = orderShift.date;
          if (!all[orderShiftDate]) {
            all[orderShiftDate] = [];
          }

          all[orderShiftDate].push(orderShift);
          return all;
        }, {})
      }))
    }));
  }, [includeShiftEndDate, projectsWithAllShiftsDates, singleDayViewDate, timeRange]);

  const projectById = useMemo(() => mappedArray(projects, ({ id }) => id), [projects]);

  const projectsByDate = useMemo(() => {
    const dates = (projects || []).reduce((all, project) => {
      const datesInRange =
        generateRange({ start: project.date, end: project.endDate || project.date }).map((dateAsMoment) =>
          moment(dateAsMoment).format(API_DATE_FORMAT)
        ) || [];
      datesInRange
        .filter((currDate) => !!currDate)
        .forEach((currDate) => {
          if (!all[currDate]) {
            all[currDate] = [];
          }
          all[currDate].push(project);
        });
      return all;
    }, {});

    const map = {};
    Object.entries(dates).forEach(([date, projectsData]) => (map[getDate(date)] = projectsData));
    return map;
  }, [projects]);

  const shiftsByEmployee = useMemo(() => {
    const mappedShifts = {};
    projects.forEach(
      ({
        id,
        labelId,
        name,
        shifts,
        clientBranchId,
        clientAdminId,
        locationId: projectLocationId,
        isWeeklyPlanner,
        isMultiDay,
        date: projectDate,
        endDate: projectEndDate,
        tags: eventTags
      }) => {
        shifts.forEach(
          ({
            id: shiftId,
            needed,
            startTime,
            slots,
            wage,
            confirmations,
            positionId,
            branchCostCenterId,
            isOpenShift,
            shiftGroupId,
            shiftTags,
            // For shifts, `locationId` can represent the sub-location (area). We remap it to `areaId` for clarity downstream.
            locationId: areaId
          }) => {
            (slots || []).forEach(({ employeeId, ...slot }) => {
              if (!employeeId) {
                return;
              }
              const formattedDate = getDate(startTime);
              const key = `${formattedDate}-${employeeId}`;

              const isArrivalConfirmed =
                confirmations?.length > 0 &&
                confirmations[confirmations.length - 1].employees?.length > 0 &&
                confirmations[confirmations.length - 1].employees?.find(
                  (employee) => employee.employeeId === employeeId
                )?.isConfirmed;

              mappedShifts[key] = [
                ...(mappedShifts[key] || []),
                {
                  projectId: id,
                  labelId,
                  clientBranchId,
                  clientAdminId,
                  locationId: projectLocationId,
                  branchCostCenterId,
                  name,
                  isArrivalConfirmed,
                  confirmations,
                  positionId,
                  shiftId,
                  needed,
                  wage,
                  slotId: slot.id,
                  areaId,
                  isWeeklyPlanner,
                  isMultiDay,
                  isOpenShift,
                  shiftGroupId,
                  shiftTags,
                  eventTags,
                  ...(isMultiDay ? { projectDate, projectEndDate: projectEndDate || projectDate } : {}),
                  ...slot
                }
              ];
            });
          }
        );
      }
    );

    return mappedShifts;
  }, [projects]);

  return {
    storeKey,
    isLoading,
    projects,
    employeesMap,
    projectById,
    projectsWithAllShiftsDates,
    projectsByDate,
    shiftsByEmployee,
    unbookings: data?.unbookings,
    refetch
  };
};

export default useProjects;
